import { Button, Dialog, Icon, Sheet, useIsMobile } from '@forecastify/ui';
import { useT } from '../../i18n';
import { usePageHeader } from '../page-header';
import { Breadcrumb } from '../breadcrumb';
import { costCrumbs } from './money-crumbs';
import {
  ExternalCostDetail,
  type ExternalCostDetailData,
  type ExternalCostDetailHandlers,
} from './external-cost-detail';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C62 · The external cost in three frames. The period surface's shape.      │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The same three the task, contract, line item and period already have, and for the same reason:
 * three details that open differently teach three habits. The mockup's own `ecMode` carries
 * `drawer`, `modal` and `full`, so this is the prototype's shape as well as the product's.
 *
 * ⚠️ **The drawer is the default**, which is the mockup's `openExc`. A cost is usually opened from
 * the project's *Külső költségek* tab to check one figure, and a full-page navigation to answer
 * *"how much is this again"* loses the table somebody was reading.
 */
export type ExternalCostMode = 'drawer' | 'modal' | 'full';

export interface ExternalCostSurfaceProps {
  readonly mode: ExternalCostMode;
  readonly open: boolean;
  readonly data: ExternalCostDetailData;
  readonly handlers: ExternalCostDetailHandlers;
  readonly onModeChange: (mode: ExternalCostMode) => void;
  readonly onClose: () => void;
  /**
   * ⚠️ Workspace-relative, for the breadcrumb — R-38. ux-pass-2, 14 Oct: *"breadcrumb is missing"*.
   * The trail is `money-crumbs.ts`'s, once for all three frames, as on the line item.
   */
  readonly onNavigate?: (path: string) => void;
}

export function ExternalCostSurface(props: ExternalCostSurfaceProps) {
  const mobile = useIsMobile();
  if (props.mode === 'full') return <FullPage {...props} />;
  /* ⚠️ 17 Oct 2026 · on a phone a modal is the full-screen drawer — the product owner. */
  if (props.mode === 'modal' && !mobile) return <Modal {...props} />;
  return <Drawer {...props} />;
}

function Drawer({
  open,
  data,
  handlers,
  onModeChange,
  onClose,
  onNavigate,
}: ExternalCostSurfaceProps) {
  const t = useT();

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={<Breadcrumb crumbs={costCrumbs(data.cost, t, onNavigate)} />}
      width="min(560px, 96vw)"
      closeLabel={t('common.close')}
      /* 17 Oct 2026 · no mode buttons on a phone: the drawer is the only frame there. */
      actions={
        useIsMobile()
          ? []
          : [
              {
                label: t('task.mode.modal'),
                icon: 'maximize',
                onSelect: () => {
                  onModeChange('modal');
                },
              },
            ]
      }
    >
      <ExternalCostDetail data={data} handlers={handlers} wide={false} />
    </Sheet>
  );
}

function Modal({
  open,
  data,
  handlers,
  onModeChange,
  onClose,
  onNavigate,
}: ExternalCostSurfaceProps) {
  const t = useT();

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      bare
      /* ⚠️ Item 36 · the same width as its siblings. A detail modal narrower than the others is a
         surface somebody has to re-learn. */
      width="min(1320px, 94vw)"
      closeLabel={t('common.close')}
    >
      <div className="flex h-12 shrink-0 items-center gap-2 border-b px-4">
        <Breadcrumb
          copyValue={data.cost.identifier}
          crumbs={costCrumbs(data.cost, t, onNavigate)}
        />

        <div className="flex-1" />

        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={t('task.mode.drawer')}
          title={t('task.mode.drawer')}
          onClick={() => {
            onModeChange('drawer');
          }}
        >
          <Icon name="minimize" size={15} />
        </Button>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={t('task.mode.full')}
          title={t('task.mode.full')}
          onClick={() => {
            onModeChange('full');
          }}
        >
          <Icon name="maximize" size={15} />
        </Button>
        <Button variant="ghost" size="icon-sm" aria-label={t('common.close')} onClick={onClose}>
          <Icon name="x" size={15} />
        </Button>
      </div>

      {/* ⚠️ A flex column the detail FILLS rather than the scroller — 14 Oct. The detail's header
          stays put and its two halves scroll apart (`DetailFrame` in `detail-chrome.tsx`); the
          overflow is only the fallback for a body that is not bounded. */}
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        <ExternalCostDetail data={data} handlers={handlers} wide />
      </div>
    </Dialog>
  );
}

function FullPage({ data, handlers, onModeChange, onClose, onNavigate }: ExternalCostSurfaceProps) {
  const t = useT();

  usePageHeader(
    () => (
      <>
        <Button
          variant="ghost"
          size="icon-sm"
          className="-ml-1.5"
          aria-label={t('task.action.back')}
          onClick={onClose}
        >
          <Icon name="chevron-left" size={16} />
        </Button>
        <Breadcrumb
          copyValue={data.cost.identifier}
          crumbs={costCrumbs(data.cost, t, onNavigate)}
        />

        <div className="flex-1" />

        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={t('task.mode.drawer')}
          title={t('task.mode.drawer')}
          onClick={() => {
            onModeChange('drawer');
          }}
        >
          <Icon name="minimize" size={15} />
        </Button>
      </>
    ),
    /* ⚠️⚠️ **VALUES, NOT CALLBACKS** — `hard-won.md` C24-1 records the 2,056-render loop that
       listing the handlers here caused, and its symptom: the address changed and the previous
       screen stayed on it. */
    [
      data.cost.identifier,
      data.cost.name,
      data.cost.projectName,
      data.cost.linkKind,
      data.cost.contractIdentifier,
      data.cost.lineItemName,
      data.cost.occurrenceStartsOn,
    ],
  );

  return (
    /* ⚠️ No `data-screen` here — the ROUTE carries it, so the page is marked even when the number
       matches nothing. See `invoices-route.tsx`. */
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
      <div className="mx-auto flex min-h-0 w-full max-w-[1160px] flex-1 flex-col">
        <ExternalCostDetail data={data} handlers={handlers} wide />
      </div>
    </div>
  );
}
